// Parses an admin-supplied promo video URL into the pieces the volunteer page
// needs to render a thumbnail card. Pure and total — never throws, and coerces
// the app's placeholder strings ("null"/"undefined") and blanks to null first.
//
// Supported providers:
//   YouTube — watch?v=<id>, youtu.be/<id>, /embed/<id>, /shorts/<id> (11-char id)
//             → thumbnail from i.ytimg.com, embed on youtube.com
//   Vimeo   — vimeo.com/<digits> (optionally /video/<digits>)
//             → no cheap public thumbnail, so thumbnailUrl is '' (caller renders
//               a branded frame instead), embed on player.vimeo.com
// Anything else → null.

export interface ParsedVideo {
	provider: 'youtube' | 'vimeo';
	id: string;
	thumbnailUrl: string;
	embedUrl: string;
}

const YOUTUBE_PATTERN = /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const VIMEO_PATTERN = /vimeo\.com\/(?:video\/)?(\d+)/;

export const parseVideoUrl = (raw: string): ParsedVideo | null => {
	if (typeof raw !== 'string') return null;
	const trimmed = raw.trim();
	if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') return null;

	const youtube = YOUTUBE_PATTERN.exec(trimmed);
	if (youtube) {
		const id = youtube[1];
		return {
			provider: 'youtube',
			id,
			thumbnailUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
			embedUrl: `https://www.youtube.com/embed/${id}`,
		};
	}

	const vimeo = VIMEO_PATTERN.exec(trimmed);
	if (vimeo) {
		const id = vimeo[1];
		return {
			provider: 'vimeo',
			id,
			thumbnailUrl: '',
			embedUrl: `https://player.vimeo.com/video/${id}`,
		};
	}

	return null;
};

export default parseVideoUrl;
